import type { ReactNode } from "react";
import { createContext, useContext } from "react";

/** The control's validation status inside a FormItem. */
export type FieldStatus = "error" | "warning" | undefined;

interface FormItemStatusValue {
  status: FieldStatus;
  /** The short warning message, so a control can title its explanation. */
  warningMessage: ReactNode | undefined;
  /**
   * The long-form reason behind a `warning`, for the control to hang off its
   * in-field status icon. The short message stays in the line below the field;
   * this is the sentence that wouldn't fit there.
   */
  warningExplanation: ReactNode | undefined;
}

/**
 * The visible validation status of the control inside a FormItem — "error"
 * (red), "warning" (orange), or undefined. Controls read it to paint their
 * border/underline and show a matching in-field status icon.
 */
const FormItemStatusContext = createContext<FormItemStatusValue>({
  status: undefined,
  warningMessage: undefined,
  warningExplanation: undefined,
});

export const FormItemErrorProvider = FormItemStatusContext.Provider;

/** The full status — use when a control shows a per-status icon. */
export const useFormItemStatus = () => useContext(FormItemStatusContext).status;

/** The short warning message — the title for the explanation below it. */
export const useFormItemWarningMessage = () =>
  useContext(FormItemStatusContext).warningMessage;

/** The explanation to hang off that icon, if the FormItem supplied one. */
export const useFormItemWarningExplanation = () =>
  useContext(FormItemStatusContext).warningExplanation;

/** Back-compat boolean: true only for a visible error (red border/underline). */
export const useIsFormItemError = () =>
  useContext(FormItemStatusContext).status === "error";
